import React from 'react';
import { useTranslation } from 'react-i18next';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SyncAgentType = 'greeting' | 'extraction_result' | 'extraction_error';

export interface IdentifiedItem {
  part_name?: string;
  part_area?: string;
  part_category?: string;
  damage_type?: string;
  damage_severity?: string;
  condition?: string;
  confidence_score?: number;
}

export type IdentifiedItemLike = string | IdentifiedItem;

export interface SyncAgentResponse {
  type: SyncAgentType;
  action: 'TRIGGER_DELEGATION' | null;
  message: string;
  data: {
    vin_number: string | null;
    identified_items: IdentifiedItemLike[];
  } | null;
}

/** Return true when obj looks like a SyncAgentResponse. */
export function isSyncAgentResponse(obj: any): obj is SyncAgentResponse {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.type === 'string' &&
    ['greeting', 'extraction_result', 'extraction_error'].includes(obj.type) &&
    'action' in obj &&
    'message' in obj 
  );
}


export function isSyncAgentResponseShouldDisplay(obj: any): boolean {
  if (!obj) return false;
  // Never render extraction_result cards — the follow-up agent question
  // already tells the user what was extracted and asks for what is missing.
  return obj.type !== 'extraction_result';
}
// ─── Sub-components ───────────────────────────────────────────────────────────

// ─── Main component ───────────────────────────────────────────────────────────

interface SyncAgentResultViewProps {
  response: SyncAgentResponse;
  /** Optional annotated images to display above the message text. */
  annotatedImages?: string[];
  onImageClick?: (index: number) => void;
}

const SyncAgentResultView: React.FC<SyncAgentResultViewProps> = ({ response, annotatedImages, onImageClick }) => {
  const { t } = useTranslation();

  // ── Annotated images (shown first, regardless of response type) ───────────
  const imagesBlock = annotatedImages && annotatedImages.length > 0 ? (
    <div style={{ marginBottom: '10px' }}>
      <div style={{
        fontSize: '11px', fontWeight: 600, color: '#aaa',
        letterSpacing: '0.04em', textTransform: 'uppercase',
        marginBottom: '6px', padding: '0 2px',
      }}>
        {t('chat.botMessage.annotatedImage', { defaultValue: 'Analysis Result' })}
      </div>
      <div style={{
        display: 'grid',
        gap: '12px',
        gridTemplateColumns: annotatedImages.length > 1 ? 'repeat(2, 1fr)' : '1fr',
      }}>
        {annotatedImages.map((imageUrl, index) => (
          <div key={index} style={{ position: 'relative' }}>
            <img
              src={imageUrl}
              alt={`Annotated analysis ${index + 1}`}
              style={{
                width: '100%', height: 'auto', borderRadius: '8px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                border: '1px solid #e5e7eb',
                maxHeight: '400px', objectFit: 'contain',
                cursor: onImageClick ? 'pointer' : 'default',
                transition: 'opacity 0.15s',
              }}
              onClick={() => onImageClick?.(index)}
            />
          </div>
        ))}
      </div>
    </div>
  ) : null;

  // ── Greeting ─────────────────────────────────────────────────────────────
  if (response.type === 'greeting') {
    return (
      <>
        {imagesBlock}
        <div style={{ margin: 0, fontSize: '14px', color: '#333', lineHeight: '1.55' }}>
          {response.message}
        </div>
      </>
    );
  }

  // ── Extraction error ─────────────────────────────────────────────────────
  if (response.type === 'extraction_error') {
    return (
      <>
        {imagesBlock}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '8px',
          padding: '10px 12px', borderRadius: '8px',
          backgroundColor: '#fff7ed', border: '1px solid #fed7aa',
        }}>
          <span style={{ fontSize: '16px', flexShrink: 0, lineHeight: 1.2 }}>⚠️</span>
          <span style={{ margin: 0, fontSize: '14px', color: '#333', lineHeight: '1.55' }}>
            {response.message}
          </span>
        </div>
      </>
    );
  }

  // Extraction result cards are never rendered (isSyncAgentResponseShouldDisplay returns false).
  // This branch is unreachable but kept for completeness.
  return null;
};

export default SyncAgentResultView;
